import loglevel from 'loglevel';
import { apiDateFormatter, queryString } from 'lib/format';
import toAbsoluteURL from 'lib/url-adapter/toAbsoluteURL';
import { LISTINGS_API_CONFIGURATION } from 'constants';
import request from './cache/requestCache';
import proxy from './requestProxy';
import fallback from './cache/fallbackCache';
import appConfig from '../../../config';
import { Api } from 'enums';

const { Env: ApiEnv } = Api;

const log = loglevel.getLogger('WatchAPI');
const watchApiConfig = appConfig.watchApi;

const DEFAULT_CONFIGURATION = {
    apiKey: '',
    networks: 'espn1,espn2,espn3,espnu,sec,secplus,espnews,espndeportes,longhorn,goalline,buzzerbeater,acc,accextra,secplus',
    resultsLimit: 200
};

export const getServiceUrl = (env = ApiEnv.PROD) => toAbsoluteURL(LISTINGS_API_CONFIGURATION[env].serviceUrl);

const config = { ...DEFAULT_CONFIGURATION, ...watchApiConfig };

export function getListings({ types, networks, id, eventId, airingId, categories, subcategories, dates, limit = config.resultsLimit, apiEnv = ApiEnv.PROD }, listingsHref) {
    const typesQuery = types ? types.join(',') : '';
    const networksQuery = (networks === '') ? config.networks : null;
    const categoriesQuery = categories ? categories.join(',') : '';
    const subcategoriesQuery = subcategories ? subcategories.join(',') : '';
    const datesQuery = dates ? apiDateFormatter(...dates) : 0;
    const limitQuery = limit;
    // Need ability to pass in listingsHref for conference channels (from state)
    const baseUrl = listingsHref || `${getServiceUrl(apiEnv)}/listings`;

    const qs = queryString({
        types: typesQuery,
        networks: networksQuery,
        id,
        eventId,
        airingId,
        categories: categoriesQuery,
        subcategories: subcategoriesQuery,
        dates: datesQuery,
        limit: limitQuery
    });
    // if a baseUrl is passed in, it could potentially already have queryParamters.  Need to replace ? with & if so
    const url = (baseUrl.indexOf('?') !== -1) ? `${baseUrl}${qs.replace('?', '&')}` : `${baseUrl}${qs}`;

    log.debug(`getClient: ${url}`);
    return request(url)
        .use(proxy)
        .then((response) => {
            const listings = response.body.listings || [];
            fallback.set(url, listings);
            return listings;
        })
        .catch((err) => {
            log.error('getListings:', err.originalError);
            return fallback.get(url);
        });
}

export function getListingsBySubcategory({ types, networks, id, eventId, airingId, subcategory, dates, limit = config.resultsLimit, apiEnv = ApiEnv.PROD }) {
    const typesQuery = types ? types.join(',') : '';
    const networksQuery = (networks === '') ? config.networks : networks.join(',');
    let datesQuery = 0;
    if (typeof dates === 'string' && /^\d{8}/.test(dates)) {
        datesQuery = dates;
    } else if (Array.isArray(dates)) {
        datesQuery = apiDateFormatter(...dates);
    }
    const limitQuery = limit;
    const baseUrl = `${getServiceUrl(apiEnv)}/subcategories/${subcategory}/listings`;

    const qs = queryString({
        types: typesQuery,
        networks: networksQuery,
        id,
        eventId,
        airingId,
        dates: datesQuery,
        limit: limitQuery
    });
    // if a baseUrl is passed in, it could potentially already have queryParamters.  Need to replace ? with & if so
    const url = (baseUrl.indexOf('?') !== -1) ? `${baseUrl}${qs.replace('?', '&')}` : `${baseUrl}${qs}`;

    return request(url)
        .use(proxy)
        .then((response) => {
            const subcategories = response.body.subcategories || [];
            const listings = subcategories[0] && subcategories[0].listings;
            fallback.set(url, listings);
            return listings;
        })
        .catch((err) => {
            log.error('getListingsBySubcategory:', err.originalError);
            return fallback.get(url);
        });
}

export function getTopCategories(apiEnv = ApiEnv.PROD) {
    const url = `${getServiceUrl(apiEnv)}/categories/top`;

    return request(url)
        .use(proxy)
        .then((response) => {
            const categories = response.body.categories || [];
            fallback.set(url, categories);
            return categories;
        })
        .catch((err) => {
            log.error('getTopCategories:', err.originalError);
            return fallback.get(url);
        });
}

export function getPrioritizedEventFromListings(listings, lang) {
    let event;
    let matchedLangLiveNonE3;
    let matchedLiveE3;
    let matchedLangLiveE3;
    let matchedLiveNonE3;
    let matchedLangNonE3;
    let matchedLangE3;
    let matchedLangReplay;
    let index = 0;
    const len = listings.length;

    for (; index < len; index += 1) {
        const content = listings[index];
        const { type, lang: sourceLang, broadcasts: [{ ncsId }] } = content;
        event = content;
        if (len === 1) {
            break;
        }
        // prefer same lang, live and non-E3
        if (sourceLang === lang) {
            if (type === 'live') {
                if (ncsId !== 'n360') {
                    matchedLangLiveNonE3 = event;
                    break;
                } else if (!matchedLangLiveE3) {
                    matchedLangLiveE3 = event;
                }
            } else if (type === 'replay') {
                if (!matchedLangReplay) {
                    matchedLangReplay = event;
                }
            } else if (ncsId !== 'n360') {
                if (!matchedLangNonE3) {
                    matchedLangNonE3 = event;
                }
            } else if (!matchedLangE3) {
                matchedLangE3 = event;
            }
        } else if (type === 'live') {
            if (ncsId !== 'n360') {
                if (!matchedLiveNonE3) {
                    matchedLiveNonE3 = event;
                }
            } else if (!matchedLiveE3) {
                matchedLiveE3 = event;
            }
        }
    }

    return (
        matchedLangLiveNonE3 ||
        matchedLangLiveE3 ||
        matchedLiveNonE3 ||
        matchedLiveE3 ||
        matchedLangReplay ||
        matchedLangNonE3 ||
        matchedLangE3 ||
        event
    );
}



// WEBPACK FOOTER //
// ./app/lib/api/WatchAPI.js